import type { RoomMemberEntity } from '@/helpers/types'
import { useRoomStore } from '@/stores/room'

interface UseMemberStateProps<TKey> {
  memberId: string
  subscriptionKey: TKey
}

/**
 * Returns the state of a member for a given subscription key
 * @param memberId - The ID of the member
 * @param subscriptionKey - The key of the subscription
 * @returns The state of the member for the given subscription key
 * @throws An error if the member state slice is not found
 * @note will only re-render when the member state slice changes
 */
export const useMemberState = <
  TKey extends NonNullable<keyof RoomMemberEntity>,
>({
  memberId,
  subscriptionKey,
}: UseMemberStateProps<TKey>): RoomMemberEntity[TKey] => {
  const memberStateSlice = useRoomStore(
    state => state.membersMap.get(memberId)?.[subscriptionKey],
  )

  if (memberStateSlice === undefined) {
    throw new Error(
      `Member state slice not found for memberId: ${memberId} and subscriptionKey: ${subscriptionKey}`,
    )
  }

  return memberStateSlice
}
